import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
  requireSubscription?: boolean;
}

export const ProtectedRoute = ({
  children,
  requireOnboarding = true,
  requireSubscription = true,
}: ProtectedRouteProps) => {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="relative h-16 w-16">
          <div className="absolute inset-0 rounded-full border-4 border-gray-800" />
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-yellow-500 border-r-yellow-400 animate-spin" />
        </div>
      </div>
    );
  }

  // Not authenticated — redirect to login, preserving intended destination
  if (!isAuthenticated || !user) {
    const redirectUrl = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} replace />;
  }

  // Suspended — redirect to suspended page regardless of where they're trying to go
  if (user.is_suspended && location.pathname !== '/suspended') {
    return <Navigate to="/suspended" replace />;
  }

  // Onboarding incomplete — redirect to onboarding
  if (requireOnboarding && !user.onboarding_complete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Onboarding complete but navigating to onboarding — redirect to dashboard
  if (user.onboarding_complete && location.pathname === '/onboarding') {
    return <Navigate to={'/dashboard'} replace />;
  }

  // No active subscription — redirect to /subscribe
  if (
    requireSubscription &&
    user.onboarding_complete &&
    !user.plan &&
    location.pathname !== '/subscribe'
  ) {
    return <Navigate to="/subscribe" replace />;
  }

  return <>{children}</>;
};